import { useCallback, useMemo, useState } from 'react'

import { usePSUser } from '../../hooks'
import { CalendarFilterType } from '../../models/CalendarFilter'
import { Item } from '../../models/Item'
import { User } from '../../models/User'
import { AvailabilityFilter, Duration, getDurationFromDurationValue } from '../../utils/calendar'
import Button from '../app/design/Button'
import Modal from '../app/design/Modal'
import { CalendarDurationModal } from './CalendarDurationModal'
import { CalendarFilterPillButton } from './CalendarFilterPillButton'
import { CalendarSelectCards } from './CalendarSelectCards'

type Props = {
    availabilityFilter: AvailabilityFilter
    pros: User[]
    items: Item[]
}

export const CalendarAvailabilityFilters = ({ availabilityFilter, pros, items }: Props) => {
    const me = usePSUser()

    const isTeamAccount = useMemo(() => me?.type === 'company', [me?.type])

    const [showSelectServiceModal, setShowSelectServiceModal] = useState(false)
    const [showSelectProModal, setShowSelectProModal] = useState(false)
    const [showSelectDurationModal, setShowSelectDurationModal] = useState(false)
    const [localFilter, setLocalFilter] = useState(availabilityFilter)

    const selectedItem = useMemo(
        () =>
            localFilter.services.length > 0
                ? items.find((item) => item.id === localFilter.services[0])
                : null,
        [localFilter.services, items]
    )

    const isDurationDisabled = useMemo(
        () =>
            (selectedItem?.type === 'class' && !!selectedItem?.duration) ||
            (selectedItem?.type === 'reservation' &&
                !!selectedItem?.duration &&
                !!selectedItem?.startDate),
        [selectedItem]
    )

    const onCancel = useCallback(() => {
        setShowSelectServiceModal(false)
        setShowSelectProModal(false)
        setLocalFilter(availabilityFilter)
    }, [availabilityFilter])

    const setAvailabilityFilter = (filter: AvailabilityFilter) => {
        console.log('[todo] setAvailabilityFilter', filter)
    }

    const onDone = useCallback(() => {
        setShowSelectServiceModal(false)
        setShowSelectProModal(false)

        setAvailabilityFilter({
            ...localFilter,
            duration: selectedItem?.duration
                ? getDurationFromDurationValue(selectedItem.duration)
                : { value: 0, label: 'Duration' },
        })
    }, [localFilter, selectedItem])

    const handleDurationModalDone = useCallback(
        (duration: Duration) => {
            setShowSelectDurationModal(false)
            setAvailabilityFilter({ ...localFilter, duration })
        },
        [localFilter]
    )

    const servicesFilterLabel = useMemo(() => {
        const length = availabilityFilter.services.length

        if (length === 0) return 'Services'
        if (length === 1)
            return (
                items.find((item) => item.id === availabilityFilter.services[0])?.name || 'Services'
            )
        return `${length} Services`
    }, [availabilityFilter.services, items])

    const prosFilterLabel = useMemo(() => {
        const length = availabilityFilter.pros.length

        if (length === 0) return 'Pros'
        if (length === 1)
            return pros.find((pro) => pro.id === availabilityFilter.pros[0])?.name || 'Pros'
        return `${length} Pros`
    }, [availabilityFilter.pros, pros])

    return (
        <div className="flex gap-1">
            <CalendarFilterPillButton
                label={servicesFilterLabel}
                active={availabilityFilter.services.length > 0}
                open={showSelectServiceModal}
                onClick={() => setShowSelectServiceModal(true)}
            />

            {isTeamAccount && (
                <CalendarFilterPillButton
                    label={prosFilterLabel}
                    active={availabilityFilter.pros.length > 0}
                    open={showSelectProModal}
                    onClick={() => setShowSelectProModal(true)}
                />
            )}

            <CalendarFilterPillButton
                label={localFilter.duration.label}
                active={localFilter.duration.label !== 'Duration'}
                open={showSelectDurationModal}
                disabled={isDurationDisabled}
                onClick={() => setShowSelectDurationModal(true)}
            />

            <Modal
                title="Services"
                isOpen={showSelectServiceModal}
                onClose={onCancel}
                footer={
                    <div className="flex w-full gap-4">
                        <Button
                            className="mr-auto"
                            disabled={!localFilter.services.length}
                            onClick={() => setLocalFilter((f) => ({ ...f, services: [] }))}
                        >
                            Clear
                        </Button>

                        <Button onClick={onCancel}>Cancel</Button>
                        <Button intent="primary" onClick={onDone}>
                            Done
                        </Button>
                    </div>
                }
            >
                <div className="flex flex-col gap-3">
                    <CalendarSelectCards
                        type={CalendarFilterType.Services}
                        list={items}
                        multiSelect={false}
                        selected={localFilter.services}
                        onSelect={(ids: string[]) =>
                            setLocalFilter({ ...localFilter, services: ids })
                        }
                    />
                </div>
            </Modal>

            <Modal
                title="Pros"
                isOpen={showSelectProModal}
                onClose={onCancel}
                footer={
                    <div className="flex w-full gap-4">
                        <Button
                            className="mr-auto"
                            disabled={!localFilter.pros.length}
                            onClick={() => setLocalFilter((f) => ({ ...f, pros: [] }))}
                        >
                            Clear all
                        </Button>

                        <Button onClick={onCancel}>Cancel</Button>
                        <Button intent="primary" onClick={onDone}>
                            Done
                        </Button>
                    </div>
                }
            >
                <div className="flex flex-col gap-3">
                    <CalendarSelectCards
                        type={CalendarFilterType.Pros}
                        list={pros}
                        selected={localFilter.pros}
                        onSelect={(ids: string[]) => setLocalFilter({ ...localFilter, pros: ids })}
                    />
                </div>
            </Modal>

            <CalendarDurationModal
                isOpen={showSelectDurationModal}
                availabilityFilter={availabilityFilter}
                itemType={selectedItem?.type}
                onDone={(duration) => handleDurationModalDone(duration)}
                onClose={() => setShowSelectDurationModal(false)}
            />
        </div>
    )
}
